import toast from 'react-hot-toast';
import { Loader2, Upload, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Button } from 'components/ui/button';
import { useImageEditorStore } from 'components/ImageEditor/ImageEditorStoreProvider';
import { AllStoreType } from 'components/ImageEditor/store/imageEditor.type';
import { ImageGenService } from 'features/upscale-image/services/image-gen.api';

export default function ObjectReplacerForm() {
  const [token, setToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const navigate = useNavigate();
  const { setImageUrl, imageUrl, clearLines } =
    useImageEditorStore<AllStoreType>((state) => state);
  const [prompt, setPrompt] = useState('');

  const { mutateAsync: replaceObjectAsync } = useMutation({
    mutationFn: (formData: FormData) =>
      ImageGenService.replaceObject(formData, token),
    onSuccess: (data) => {
      toast.success('Image Replaced Successfully');

      clearLines();
      setImageUrl(data.data);
    },
  });

  const onSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!imageUrl) {
      toast.error('Please upload an image');
      return;
    }

    navigate('/object-replacer/result');
  };

  const validateFile = (file: File) => {
    setImageUrl(file);
    return file;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const file = e.target.files?.[0];
    if (file) {
      validateFile(file);
    }
  };

  const handleRemoveImage = () => {
    setImageUrl(null);
    clearLines();
    navigate('/object-replacer/get-started');
  };

  const handleMaskAndFormData = async () => {
    try {
      if (!prompt) {
        toast.error('Please enter a prompt');
        return;
      }

      // Create FormData and append PNG
      // Create FormData and append PNG
      const formData = new FormData();

      if (imageUrl) {
        if (typeof imageUrl === 'string') {
          // Fetch image from URL and convert to File
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const file = new File([blob], 'image.png', { type: 'image/png' });
          formData.append('image', file);
        } else {
          formData.append('image', imageUrl);
        }
      }

      return formData;
    } catch (error) {
      console.error('Error processing mask:', error);
      toast.error('Error processing mask');
    }
  };

  const handleReplaceClick = async () => {
    if (!imageUrl || !prompt) return;

    try {
      setIsLoading(true);
      const formData = await handleMaskAndFormData();

      if (!formData) return;

      formData.append('prompt', prompt);

      if (formData) {
        setRefreshReCaptcha((r) => !r);

        await replaceObjectAsync(formData);
      }
    } catch (error) {
      console.error('Error during replace operation:', error);
      toast.error('Failed to process image');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!imageUrl) {
      navigate('/object-replacer/get-started');
    }
  }, [imageUrl]);

  useEffect(() => {
    return () => {
      if (window.location.pathname !== '/object-replacer/result') {
        setImageUrl(null);
        clearLines();
      }
    };
  }, []);

  return (
    <div className="bg-background text-text-primary flex flex-col rounded-md p-3 px-4 col-span-3 overflow-y-auto max-h-[calc(100vh-100px)]">
      <p className="text-text-secondary uppercase text-sm">IMAGE TYPE</p>

      <div className="mt-3 flex flex-1 flex-col gap-4">
        <div className="flex flex-col gap-4">
          {imageUrl ? (
            <>
              <img
                src={
                  typeof imageUrl === 'string'
                    ? imageUrl
                    : URL.createObjectURL(imageUrl)
                }
                className="lg:block hidden w-full h-full object-cover rounded-md"
              />
              <button
                onClick={handleRemoveImage}
                className="flex text-sm items-center gap-2 justify-between border-2 border-dashed border-secondary text-primary text-[17px] rounded-md p-2 "
              >
                <span>
                  {typeof imageUrl === 'string'
                    ? imageUrl.slice(0, 10) + '...'
                    : imageUrl.name}
                </span>
                <X size={16} />
              </button>
            </>
          ) : (
            <button className="relative text-sm flex items-center justify-center gap-2 w-full border-2 border-dashed border-secondary text-primary text-[17px] rounded-md p-2 ">
              <label
                htmlFor="fileInput"
                className="absolute inset-0 cursor-pointer"
              >
                <input
                  id="fileInput"
                  type="file"
                  accept="image/png, image/jpeg, image/jpg"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
              <Upload size={16} /> Upload Image
            </button>
          )}
        </div>

        {window.location.pathname === '/object-replacer/get-started' && (
          <div className="w-full">
            <Button
              onClick={onSubmit}
              disabled={!imageUrl}
              className="w-full gap-3 mt-2 text-sm"
              variant={'customPrimary'}
            >
              Upload
            </Button>
          </div>
        )}

        {imageUrl && window.location.pathname === '/object-replacer/result' && (
          <>
            <p className="text-text-secondary uppercase text-sm">IMAGE EDIT</p>
            <div className="w-full flex flex-col gap-6">
              <div>
                <div className="text-sm mb-2">
                  Enter your replacement prompt
                </div>
                <textarea
                  placeholder="e.g. Replace blue sky with sunset."
                  className="w-full border-2 border-dashed border-secondary bg-background-main rounded-md p-2 resize-none"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </div>

              <Button
                variant={'customPrimary'}
                disabled={
                  !imageUrl || !prompt || isLoading
                }
                onClick={handleReplaceClick}
              >
                {isLoading ? (
                  <div className="animate-spin">
                    <Loader2 className="animate-spin" size={16} />
                  </div>
                ) : (
                  'Replace'
                )}
              </Button>
            </div>
          </>
        )}
      </div>

      <GoogleReCaptcha
        action="KaiRecaptcha0911"
        onVerify={setToken}
        refreshReCaptcha={refreshReCaptcha}
      />
    </div>
  );
}
